import { getFunctions, httpsCallable } from 'firebase/functions';
import type { EmailResult, SendConsumptionEmailRequest, SendBookingRejectionEmailRequest, SendBookingApprovedEmailRequest, ResendStudentCredentialsResponse } from '@/shared/types/email.types';

/**
 * Initialize Functions client
 */
const functions = getFunctions(undefined, 'europe-west1'); // Use European region

/**
 * Callable reference to the email functions
 */
const sendEnergyConsumptionEmailFn = httpsCallable<
  SendConsumptionEmailRequest,
  EmailResult
>(functions, 'sendEnergyConsumptionEmail');

const sendBookingRejectionEmailFn = httpsCallable<
  SendBookingRejectionEmailRequest,
  EmailResult
>(functions, 'sendBookingRejectionEmail');

const sendBookingApprovedEmailFn = httpsCallable<
  SendBookingApprovedEmailRequest,
  EmailResult
>(functions, 'sendBookingApprovedEmail');

const resendStudentCredentialsFn = httpsCallable<
  { bookingId: string; portalUrl: string },
  ResendStudentCredentialsResponse & { failedEmails?: string[] }
>(functions, 'resendStudentCredentials');

/**
 * Send energy consumption notification email
 */
export async function sendConsumptionEmail(
  data: SendConsumptionEmailRequest
): Promise<EmailResult> {
  try {
    const result = await sendEnergyConsumptionEmailFn(data);
    return result.data;
  } catch (error: unknown) {
    console.error('Error calling email function:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to send email';

    return {
      success: false,
      error: errorMessage,
      retryCount: 0,
    };
  }
}

/**
 * Send booking rejection notification email
 */
export async function sendBookingRejectionEmail(
  data: SendBookingRejectionEmailRequest
): Promise<EmailResult> {
  try {
    const result = await sendBookingRejectionEmailFn(data);
    return result.data;
  } catch (error: unknown) {
    console.error('Error calling booking rejection email function:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to send email';

    return {
      success: false,
      error: errorMessage,
      retryCount: 0,
    };
  }
}

/**
 * Send booking approved notification email (credentials + stay summary)
 */
export async function sendBookingApprovedEmail(
  data: SendBookingApprovedEmailRequest
): Promise<EmailResult> {
  try {
    const result = await sendBookingApprovedEmailFn(data);
    return result.data;
  } catch (error: unknown) {
    console.error('Error calling booking approved email function:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to send email';

    return {
      success: false,
      error: errorMessage,
      retryCount: 0,
    };
  }
}

/**
 * Regenerate and re-email student credentials for an approved booking.
 * Returns the new credentials so the admin UI can re-display them.
 */
export async function resendStudentCredentials(
  bookingId: string,
  portalUrl: string
): Promise<ResendStudentCredentialsResponse & { failedEmails?: string[] }> {
  const result = await resendStudentCredentialsFn({ bookingId, portalUrl });
  return result.data;
}
